import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import {
    Video,
    Copy,
    CheckCircle2,
    Loader2,
    Search,
    RefreshCw,
    User,
    Calendar,
} from 'lucide-react';
import { toast } from 'react-toastify';
import classNames from 'classnames';

interface ZoomMeeting {
    id: number;
    date: string;
    time: string;
    patient_name: string;
    doctor_name: string;
    meeting_id: string;
    start_url: string;
    join_url: string;
    status: string;
    created_at: string;
}

const AdminZoomManagement: React.FC = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [meetings, setMeetings] = useState<ZoomMeeting[]>([]);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const meetingsRes = await api.get('/admin/settings/zoom/meetings');
            setMeetings(meetingsRes.data);
        } catch (err) {
            console.error('Failed to fetch Zoom meetings', err);
            toast.error(t('admin.update_failed'));
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.info(t('admin.zoom.copy_success'));
        } catch {
            toast.error(t('admin.update_failed'));
        }
    };

    const filteredMeetings = meetings.filter(m => 
        m.patient_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.doctor_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (m.meeting_id ?? '').includes(searchQuery)
    );

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <Loader2 className="w-10 h-10 animate-spin text-coral" />
                <p className="mt-4 text-gray-500 font-medium">{t('admin.zoom.loading')}</p>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                        <Video className="text-coral" size={28} />
                        {t('admin.zoom.title')}
                    </h1>
                    <p className="text-gray-500 mt-1">{t('admin.zoom.desc_page')}</p>
                </div>
                <button 
                    onClick={fetchData}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors shadow-sm"
                >
                    <RefreshCw size={16} />
                    {t('admin.zoom.auto_refresh')}
                </button>
            </div>

            {/* Meetings List */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
                        <div className="p-4 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <h3 className="font-bold text-gray-800 flex items-center gap-2">
                                <CheckCircle2 size={18} className="text-emerald-500" />
                                {t('admin.zoom.meetings_list')}
                                <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full ml-1">
                                    {filteredMeetings.length}
                                </span>
                            </h3>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                <input 
                                    type="text" 
                                    placeholder={t('admin.zoom.search_placeholder')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-10 pr-4 py-2 bg-gray-50 border border-gray-100 rounded-xl text-sm w-full md:w-64 focus:ring-2 focus:ring-coral/20 outline-none transition-all"
                                />
                            </div>
                        </div>

                        {/* ▼ モバイル: カード表示（横スクロールバーを出さずに縦積み） */}
                        <div className="md:hidden divide-y divide-gray-50">
                            {filteredMeetings.map((m) => (
                                <div key={m.id} className="p-4">
                                    {/* 日時 + ステータス */}
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-gray-700 break-words">{m.date}</div>
                                            <div className="text-[11px] text-gray-400 flex items-center gap-1 mt-0.5">
                                                <Calendar size={10} className="shrink-0" /> {m.time}
                                            </div>
                                        </div>
                                        <span className="shrink-0">
                                            <StatusBadge status={m.status} />
                                        </span>
                                    </div>

                                    {/* 患者 / 医師 */}
                                    <div className="mt-3 flex flex-col gap-1">
                                        <span className="text-sm text-gray-800 flex items-center gap-1.5 font-medium break-words min-w-0">
                                            <User size={12} className="text-gray-400 shrink-0" />
                                            {m.patient_name}
                                        </span>
                                        <span className="text-[11px] text-gray-400 break-words">
                                            Dr. {m.doctor_name}
                                        </span>
                                    </div>

                                    {/* ミーティングID */}
                                    <div className="mt-3 text-xs flex items-center gap-2 flex-wrap">
                                        <span className="text-gray-400 shrink-0">{t('admin.zoom.meeting_id')}：</span>
                                        <code className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-600 break-all min-w-0">
                                            {m.meeting_id}
                                        </code>
                                    </div>

                                    {/* 操作（参加URL / ホスト開始URL のコピー） */}
                                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                                        {/* 参加URL（患者共有用）をクリップボードにコピー */}
                                        <button
                                            onClick={() => copyToClipboard(m.join_url)}
                                            title={t('admin.zoom.copy_join')}
                                            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
                                        >
                                            <Copy size={13} />
                                            {t('admin.zoom.btn_join_copy')}
                                        </button>
                                        {/* ホスト開始URL（医師用）をクリップボードにコピーして医師に共有 */}
                                        <button
                                            onClick={() => copyToClipboard(m.start_url)}
                                            title={t('admin.zoom.copy_start')}
                                            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-gray-100 text-gray-500 rounded-lg hover:bg-gray-200 transition-colors"
                                        >
                                            <Copy size={13} />
                                            {t('admin.zoom.btn_host_copy')}
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {filteredMeetings.length === 0 && (
                                <div className="px-6 py-12 text-center text-gray-400 text-sm">
                                    {t('admin.zoom.no_meetings')}
                                </div>
                            )}
                        </div>

                        {/* ▼ デスクトップ: テーブル表示 */}
                        <div className="overflow-x-auto">
                            <table className="hidden md:table w-full text-left">
                                <thead className="bg-gray-50/50 text-gray-400 text-[11px] uppercase tracking-wider font-bold">
                                    <tr>
                                        <th className="px-6 py-3">{t('admin.zoom.datetime')}</th>
                                        <th className="px-6 py-3">{t('admin.zoom.patient')} / {t('admin.zoom.doctor')}</th>
                                        <th className="px-6 py-3">{t('admin.zoom.meeting_id')}</th>
                                        <th className="px-6 py-3">{t('admin.zoom.status')}</th>
                                        <th className="px-6 py-3 text-right">{t('admin.zoom.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {filteredMeetings.map((m) => (
                                        <tr key={m.id} className="hover:bg-gray-50 transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-semibold text-gray-700">{m.date}</span>
                                                    <span className="text-[11px] text-gray-400 flex items-center gap-1">
                                                        <Calendar size={10} /> {m.time}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-sm text-gray-800 flex items-center gap-1.5 font-medium">
                                                        <User size={12} className="text-gray-400" />
                                                        {m.patient_name}
                                                    </span>
                                                    <span className="text-[11px] text-gray-400 ml-4.5">
                                                        Dr. {m.doctor_name}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <code className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-600">
                                                    {m.meeting_id}
                                                </code>
                                            </td>
                                            <td className="px-6 py-4">
                                                <StatusBadge status={m.status} />
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    {/* 参加URL（患者共有用）をクリップボードにコピー */}
                                                    <button
                                                        onClick={() => copyToClipboard(m.join_url)}
                                                        title={t('admin.zoom.copy_join')}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
                                                    >
                                                        <Copy size={13} />
                                                        {t('admin.zoom.btn_join_copy')}
                                                    </button>
                                                    {/* ホスト開始URL（医師用）をクリップボードにコピーして医師に共有 */}
                                                    <button
                                                        onClick={() => copyToClipboard(m.start_url)}
                                                        title={t('admin.zoom.copy_start')}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-gray-100 text-gray-500 rounded-lg hover:bg-gray-200 transition-colors"
                                                    >
                                                        <Copy size={13} />
                                                        {t('admin.zoom.btn_host_copy')}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredMeetings.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="px-6 py-12 text-center text-gray-400 text-sm">
                                                {t('admin.zoom.no_meetings')}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
            </div>
        </div>
    );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const { t } = useTranslation();
    const statusMap: Record<string, { labelKey: string, color: string }> = {
        'pending':          { labelKey: 'admin.zoom.status_pending',           color: 'bg-gray-100 text-gray-500' },
        'reserved':         { labelKey: 'admin.zoom.status_reserved',          color: 'bg-blue-100 text-blue-700' },
        'assigned':         { labelKey: 'admin.zoom.status_assigned',          color: 'bg-indigo-100 text-indigo-700' },
        'completed':        { labelKey: 'admin.zoom.status_completed',         color: 'bg-emerald-100 text-emerald-700' },
        'expired':          { labelKey: 'admin.zoom.status_expired',           color: 'bg-yellow-100 text-yellow-700' },
        'canceled':         { labelKey: 'admin.zoom.status_canceled',          color: 'bg-gray-100 text-gray-500' },
        'doctor_cancelled': { labelKey: 'admin.zoom.status_doctor_cancelled',  color: 'bg-orange-100 text-orange-700' },
        'admin_cancelled':  { labelKey: 'admin.zoom.status_admin_cancelled',   color: 'bg-red-100 text-red-700' },
    };

    const config = statusMap[status] || { labelKey: status, color: 'bg-gray-100 text-gray-500' };

    return (
        <span className={classNames("text-[10px] px-2 py-0.5 rounded-full font-bold", config.color)}>
            {t(config.labelKey)}
        </span>
    );
};

export default AdminZoomManagement;
